import { useRef, useEffect, useCallback } from 'react';

interface Point { x: number; y: number }
interface Trace { points: Point[]; width: number }
interface Via { x: number; y: number; radius: number; holeRadius: number }
interface SubChip { x: number; y: number; w: number; h: number; pins: number }
interface Pulse { traceIndex: number; position: number; speed: number; baseSpeed: number; length: number }

interface Circuit {
  traces: Trace[];
  vias: Via[];
  subChips: SubChip[];
  pulses: Pulse[];
  centerChip: { x: number; y: number; w: number; h: number };
}

function traceLen(pts: Point[]): number {
  let l = 0;
  for (let i = 1; i < pts.length; i++) l += Math.hypot(pts[i].x - pts[i - 1].x, pts[i].y - pts[i - 1].y);
  return l;
}

function pointAlong(pts: Point[], t: number): Point {
  const total = traceLen(pts);
  let target = t * total;
  for (let i = 1; i < pts.length; i++) {
    const seg = Math.hypot(pts[i].x - pts[i - 1].x, pts[i].y - pts[i - 1].y);
    if (target <= seg && seg > 0) {
      const f = target / seg;
      return { x: pts[i - 1].x + (pts[i].x - pts[i - 1].x) * f, y: pts[i - 1].y + (pts[i].y - pts[i - 1].y) * f };
    }
    target -= seg;
  }
  return pts[pts.length - 1];
}

function routeComplex(start: Point, tx: number, ty: number, w: number, h: number): Point[] {
  const pts: Point[] = [{ ...start }];
  let cx = start.x, cy = start.y;

  if (Math.abs(tx - cx) < 5 && Math.abs(ty - cy) < 5) { pts.push({ x: tx, y: ty }); return pts; }

  const segments = 2 + Math.floor(Math.random() * 3);
  for (let s = 0; s < segments; s++) {
    const remainX = tx - cx, remainY = ty - cy;
    if (Math.abs(remainX) < 8 && Math.abs(remainY) < 8) break;

    if (s % 2 === 0) {
      const hLen = Math.abs(remainX) * (0.2 + Math.random() * 0.4);
      cx += (remainX > 0 ? 1 : -1) * hLen;
      pts.push({ x: cx, y: cy });
      const chamfer = Math.min(Math.abs(remainY) * 0.3, 30 + Math.random() * 20);
      if (chamfer > 5) {
        cx += (remainX > 0 ? 1 : -1) * chamfer;
        cy += (remainY > 0 ? 1 : -1) * chamfer;
        pts.push({ x: cx, y: cy });
      }
    } else {
      const vLen = Math.abs(remainY) * (0.2 + Math.random() * 0.4);
      cy += (remainY > 0 ? 1 : -1) * vLen;
      pts.push({ x: cx, y: cy });
      const chamfer = Math.min(Math.abs(remainX) * 0.3, 30 + Math.random() * 20);
      if (chamfer > 5) {
        cx += (remainX > 0 ? 1 : -1) * chamfer;
        cy += (remainY > 0 ? 1 : -1) * chamfer;
        pts.push({ x: cx, y: cy });
      }
    }
  }

  if (Math.abs(cx - tx) > 3) { pts.push({ x: tx, y: cy }); cx = tx; }
  if (Math.abs(cy - ty) > 3) { pts.push({ x: cx, y: ty }); }

  for (const p of pts) {
    p.x = Math.max(3, Math.min(w - 3, p.x));
    p.y = Math.max(3, Math.min(h - 3, p.y));
  }
  return pts;
}

function generateCircuit(w: number, h: number, formRect: { w: number; h: number }): Circuit {
  const traces: Trace[] = [];
  const vias: Via[] = [];
  const subChips: SubChip[] = [];

  const cx = w / 2, cy = h / 2;
  const chipW = formRect.w + 40;
  const chipH = formRect.h + 30;
  const centerChip = { x: cx, y: cy, w: chipW, h: chipH };
  const cLeft = cx - chipW / 2, cRight = cx + chipW / 2;
  const cTop = cy - chipH / 2, cBottom = cy + chipH / 2;

  interface PinDef { x: number; y: number; tx: number; ty: number; side: 'T' | 'B' | 'L' | 'R' }
  const pins: PinDef[] = [];

  // Increased pin counts for density
  const topN = 16, botN = 16, leftN = 9, rightN = 9;

  for (let i = 0; i < topN; i++) {
    const px = cLeft + (chipW / (topN + 1)) * (i + 1);
    const spread = ((i - topN / 2) / topN) * w * 0.95;
    pins.push({ x: px, y: cTop, tx: Math.max(8, Math.min(w - 8, cx + spread + (Math.random() - 0.5) * 30)), ty: -5, side: 'T' });
  }
  for (let i = 0; i < botN; i++) {
    const px = cLeft + (chipW / (botN + 1)) * (i + 1);
    const spread = ((i - botN / 2) / botN) * w * 0.95;
    pins.push({ x: px, y: cBottom, tx: Math.max(8, Math.min(w - 8, cx + spread + (Math.random() - 0.5) * 30)), ty: h + 5, side: 'B' });
  }
  for (let i = 0; i < leftN; i++) {
    const py = cTop + (chipH / (leftN + 1)) * (i + 1);
    const spread = ((i - leftN / 2) / leftN) * h * 0.85;
    pins.push({ x: cLeft, y: py, tx: -5, ty: Math.max(8, Math.min(h - 8, cy + spread + (Math.random() - 0.5) * 30)), side: 'L' });
  }
  for (let i = 0; i < rightN; i++) {
    const py = cTop + (chipH / (rightN + 1)) * (i + 1);
    const spread = ((i - rightN / 2) / rightN) * h * 0.85;
    pins.push({ x: cRight, y: py, tx: w + 5, ty: Math.max(8, Math.min(h - 8, cy + spread + (Math.random() - 0.5) * 30)), side: 'R' });
  }

  let chipCount = 0;
  const maxChips = 12;

  for (const pin of pins) {
    const stubLen = 14;
    const stub: Point = {
      x: pin.side === 'L' ? pin.x - stubLen : pin.side === 'R' ? pin.x + stubLen : pin.x,
      y: pin.side === 'T' ? pin.y - stubLen : pin.side === 'B' ? pin.y + stubLen : pin.y,
    };

    const route = routeComplex(stub, pin.tx, pin.ty, w, h);
    const full: Point[] = [{ x: pin.x, y: pin.y }, ...route];
    const thickness = 1 + Math.random() * 1.2;
    traces.push({ points: full, width: thickness });

    // Bus companions
    if (Math.random() > 0.35) {
      const off = (4 + Math.random() * 3) * (Math.random() > 0.5 ? 1 : -1);
      const isHoriz = pin.side === 'T' || pin.side === 'B';
      traces.push({ points: full.map(p => ({ x: p.x + (isHoriz ? off : 0), y: p.y + (isHoriz ? 0 : off) })), width: thickness * 0.6 });
      if (Math.random() > 0.5) {
        const off2 = off * 2.2;
        traces.push({ points: full.map(p => ({ x: p.x + (isHoriz ? off2 : 0), y: p.y + (isHoriz ? 0 : off2) })), width: thickness * 0.4 });
      }
    }

    // Sub-chips
    if (chipCount < maxChips && Math.random() > 0.45 && full.length >= 3) {
      const pos = pointAlong(full, 0.25 + Math.random() * 0.35);
      if (Math.abs(pos.x - cx) > chipW / 2 + 15 || Math.abs(pos.y - cy) > chipH / 2 + 15) {
        const scW = 22 + Math.random() * 22;
        const scH = 10 + Math.random() * 10;
        subChips.push({ x: pos.x, y: pos.y, w: scW, h: scH, pins: Math.floor(scW / 7) * 2 });
        chipCount++;
        for (let b = 0; b < 1 + Math.floor(Math.random() * 2); b++) {
          const bDir = pin.side === 'T' || pin.side === 'B' ? (b === 0 ? -Math.PI / 2 : Math.PI / 2) : (b === 0 ? 0 : Math.PI);
          const angle = bDir + (Math.random() - 0.5) * 0.5;
          const bLen = 20 + Math.random() * 50;
          const bEnd = {
            x: Math.max(5, Math.min(w - 5, pos.x + Math.cos(angle) * bLen)),
            y: Math.max(5, Math.min(h - 5, pos.y + Math.sin(angle) * bLen)),
          };
          traces.push({ points: [{ ...pos }, { x: bEnd.x, y: pos.y + (bEnd.y - pos.y) * 0.3 }, bEnd], width: 0.8 });
          vias.push({ x: bEnd.x, y: bEnd.y, radius: 3 + Math.random() * 1.5, holeRadius: 1.2 });
        }
      }
    }

    // Endpoint via
    const end = full[full.length - 1];
    vias.push({ x: end.x, y: end.y, radius: 3 + Math.random() * 2, holeRadius: 1.2 + Math.random() * 0.3 });

    // Mid-trace via
    if (Math.random() > 0.55 && full.length >= 4) {
      const mid = pointAlong(full, 0.5 + (Math.random() - 0.5) * 0.2);
      if (Math.abs(mid.x - cx) > chipW / 2 + 5 || Math.abs(mid.y - cy) > chipH / 2 + 5) {
        vias.push({ x: mid.x, y: mid.y, radius: 2.5 + Math.random() * 1.5, holeRadius: 1 });
      }
    }
  }

  // Idle pulses — slow, sparse
  const pulses: Pulse[] = [];
  const eligible = traces.filter(t => traceLen(t.points) > 80);
  for (let i = 0; i < Math.min(10, Math.floor(eligible.length * 0.15)); i++) {
    const idx = traces.indexOf(eligible[Math.floor(Math.random() * eligible.length)]);
    if (idx < 0) continue;
    const speed = 0.0004 + Math.random() * 0.0008;
    pulses.push({ traceIndex: idx, position: Math.random(), speed, baseSpeed: speed, length: 0.06 + Math.random() * 0.08 });
  }

  return { traces, vias, subChips, pulses, centerChip };
}

const TRACE_COLOR = 'rgba(160, 160, 160, 0.7)';
const VIA_COLOR = 'rgba(150, 150, 150, 0.65)';
const CHIP_FILL = 'rgba(120, 120, 120, 0.12)';
const CHIP_STROKE = 'rgba(160, 160, 160, 0.5)';
const PIN_COLOR = 'rgba(150, 150, 150, 0.5)';
const GLOW = 'rgba(255, 250, 94,';

export default function CircuitBoard() {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const circuitRef = useRef<Circuit | null>(null);
  const rafRef = useRef(0);
  // Beam: when triggered, all traces get a pulse starting at position 0
  const beamPulsesRef = useRef<{ traceIndex: number; position: number; delay: number; speed: number; length: number; opacity: number }[]>([]);
  const beamHoveringRef = useRef(false);
  const beamCooldownRef = useRef(0);
  const BEAM_SPEED = 0.00078; // constant speed for all beam pulses (1.3x idle avg)

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const onEnter = () => { beamHoveringRef.current = true; };
    const onLeave = () => { beamHoveringRef.current = false; };
    canvas.addEventListener('circuit-beam-enter', onEnter);
    canvas.addEventListener('circuit-beam-leave', onLeave);
    return () => {
      canvas.removeEventListener('circuit-beam-enter', onEnter);
      canvas.removeEventListener('circuit-beam-leave', onLeave);
    };
  }, []);

  const draw = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const circuit = circuitRef.current;
    if (!circuit) return;

    const dpr = window.devicePixelRatio || 1;
    const cw = canvas.width / dpr;
    const ch = canvas.height / dpr;

    ctx.clearRect(0, 0, cw, ch);
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';

    const { centerChip: cc } = circuit;

    // Update idle pulses
    for (const pulse of circuit.pulses) {
      pulse.position += pulse.speed;
      if (pulse.position > 1 + pulse.length) pulse.position = -pulse.length;
    }

    // Fire new beam waves while hovering
    if (beamHoveringRef.current) {
      beamCooldownRef.current--;
      if (beamCooldownRef.current <= 0) {
        const newPulses = circuit.traces.map((_, i) => ({
          traceIndex: i,
          position: 0,
          delay: Math.random() * 0.08,
          speed: BEAM_SPEED,
          length: 0.06 + Math.random() * 0.08,
          opacity: 1,
        }));
        beamPulsesRef.current.push(...newPulses);
        beamCooldownRef.current = 120;
      }
    }

    // Update beam pulses — fade out when not hovering, remove when done
    beamPulsesRef.current = beamPulsesRef.current.filter((bp) => {
      if (bp.delay > 0) {
        bp.delay -= 0.002;
        return true;
      }
      bp.position += bp.speed;
      // Fade out if no longer hovering
      if (!beamHoveringRef.current) {
        bp.opacity = Math.max(0, bp.opacity - 0.012);
      }
      return bp.opacity > 0 && bp.position < 1 + bp.length;
    });

    // --- Draw traces ---
    for (const trace of circuit.traces) {
      ctx.beginPath();
      ctx.moveTo(trace.points[0].x, trace.points[0].y);
      for (let i = 1; i < trace.points.length; i++) ctx.lineTo(trace.points[i].x, trace.points[i].y);
      ctx.strokeStyle = TRACE_COLOR;
      ctx.lineWidth = trace.width;
      ctx.stroke();
    }

    // --- Draw pulses ---
    const drawPulse = (trace: Trace, position: number, length: number, alpha: number) => {
      if (traceLen(trace.points) < 40) return;
      const steps = 12;
      for (let s = 0; s < steps; s++) {
        const frac = s / steps;
        const t1 = position - length + frac * length;
        const t2 = t1 + length / steps;
        if (t2 < 0 || t1 > 1) continue;
        const p1 = pointAlong(trace.points, Math.max(0, Math.min(1, t1)));
        const p2 = pointAlong(trace.points, Math.max(0, Math.min(1, t2)));
        ctx.beginPath();
        ctx.moveTo(p1.x, p1.y);
        ctx.lineTo(p2.x, p2.y);
        ctx.strokeStyle = `${GLOW}${(frac * 0.65 * alpha).toFixed(2)})`;
        ctx.lineWidth = trace.width + 2.5 * frac;
        ctx.stroke();
      }
      if (position >= 0 && position <= 1) {
        const head = pointAlong(trace.points, position);
        ctx.beginPath();
        ctx.arc(head.x, head.y, 2, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(255, 250, 94, ${(0.85 * alpha).toFixed(2)})`;
        ctx.fill();
      }
    };

    // Idle pulses
    for (const pulse of circuit.pulses) {
      const trace = circuit.traces[pulse.traceIndex];
      if (trace) drawPulse(trace, pulse.position, pulse.length, 1);
    }

    // Beam pulses (with fade-out opacity)
    for (const bp of beamPulsesRef.current) {
      if (bp.delay > 0) continue;
      const trace = circuit.traces[bp.traceIndex];
      if (trace) drawPulse(trace, bp.position, bp.length, bp.opacity);
    }

    // --- Draw sub-chips ---
    for (const sc of circuit.subChips) {
      ctx.fillStyle = CHIP_FILL;
      ctx.fillRect(sc.x - sc.w / 2, sc.y - sc.h / 2, sc.w, sc.h);
      ctx.strokeStyle = CHIP_STROKE;
      ctx.lineWidth = 0.8;
      ctx.strokeRect(sc.x - sc.w / 2, sc.y - sc.h / 2, sc.w, sc.h);

      const ps = sc.w / (sc.pins / 2 + 1);
      ctx.fillStyle = PIN_COLOR;
      for (let p = 0; p < sc.pins / 2; p++) {
        const px = sc.x - sc.w / 2 + ps * (p + 1);
        ctx.fillRect(px - 1, sc.y - sc.h / 2 - 4, 2, 4);
        ctx.fillRect(px - 1, sc.y + sc.h / 2, 2, 4);
      }
      ctx.beginPath();
      ctx.arc(sc.x - sc.w / 2 + 3.5, sc.y - sc.h / 2 + 3.5, 1.5, 0, Math.PI * 2);
      ctx.fillStyle = 'rgba(150, 150, 150, 0.25)';
      ctx.fill();
    }

    // --- Draw vias ---
    for (const via of circuit.vias) {
      ctx.beginPath();
      ctx.arc(via.x, via.y, via.radius, 0, Math.PI * 2);
      ctx.fillStyle = VIA_COLOR;
      ctx.fill();
      ctx.beginPath();
      ctx.arc(via.x, via.y, via.holeRadius, 0, Math.PI * 2);
      ctx.fillStyle = '#FFFFFF';
      ctx.fill();
    }

    // --- Central chip (white fill + border) ---
    ctx.fillStyle = '#FFFFFF';
    ctx.fillRect(cc.x - cc.w / 2, cc.y - cc.h / 2, cc.w, cc.h);
    ctx.strokeStyle = 'rgba(150, 150, 150, 0.45)';
    ctx.lineWidth = 2;
    ctx.strokeRect(cc.x - cc.w / 2, cc.y - cc.h / 2, cc.w, cc.h);

    const drawPins = (count: number, side: 'T' | 'B' | 'L' | 'R') => {
      ctx.fillStyle = 'rgba(150, 150, 150, 0.4)';
      for (let i = 0; i < count; i++) {
        if (side === 'T' || side === 'B') {
          const px = cc.x - cc.w / 2 + (cc.w / (count + 1)) * (i + 1);
          const py = side === 'T' ? cc.y - cc.h / 2 - 8 : cc.y + cc.h / 2;
          ctx.fillRect(px - 1.5, py, 3, 8);
        } else {
          const py = cc.y - cc.h / 2 + (cc.h / (count + 1)) * (i + 1);
          const px = side === 'L' ? cc.x - cc.w / 2 - 8 : cc.x + cc.w / 2;
          ctx.fillRect(px, py - 1.5, 8, 3);
        }
      }
    };
    drawPins(16, 'T'); drawPins(16, 'B'); drawPins(9, 'L'); drawPins(9, 'R');

    ctx.beginPath();
    ctx.arc(cc.x - cc.w / 2 + 10, cc.y - cc.h / 2 + 10, 3, 0, Math.PI * 2);
    ctx.fillStyle = 'rgba(150, 150, 150, 0.25)';
    ctx.fill();

    // --- Easter egg: PCB silkscreen watermark ---
    ctx.save();
    ctx.font = '9px Gabarito, Tahoma, Geneva, sans-serif';
    ctx.font = '10px Gabarito, Tahoma, Geneva, sans-serif';
    ctx.fillStyle = 'rgba(140, 140, 140, 0.7)';
    ctx.textAlign = 'right';
    ctx.textBaseline = 'bottom';
    ctx.fillText('Engineered with \u2764 by Altyor', cw - 16, ch - 12);
    ctx.restore();

    rafRef.current = requestAnimationFrame(draw);
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const resize = () => {
      const parent = canvas.parentElement;
      if (!parent) return;
      const dpr = window.devicePixelRatio || 1;
      const rect = parent.getBoundingClientRect();
      canvas.width = rect.width * dpr;
      canvas.height = rect.height * dpr;
      canvas.style.width = `${rect.width}px`;
      canvas.style.height = `${rect.height}px`;
      const ctx = canvas.getContext('2d');
      if (ctx) ctx.scale(dpr, dpr);
      const formW = Math.min(380, rect.width * 0.7);
      const formH = Math.min(260, rect.height * 0.35);
      circuitRef.current = generateCircuit(rect.width, rect.height, { w: formW, h: formH });
    };

    resize();
    window.addEventListener('resize', resize);
    rafRef.current = requestAnimationFrame(draw);

    return () => {
      window.removeEventListener('resize', resize);
      cancelAnimationFrame(rafRef.current);
    };
  }, [draw]);

  return (
    <canvas
      ref={canvasRef}
      data-circuit
      style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }}
    />
  );
}
